import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  SelectChangeEvent
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  CloudSync as CollectIcon
} from '@mui/icons-material';
import axios from 'axios';

interface UsageTotals { messagesSent: number; messagesReceived: number; segments: number; cost: number; activeClients: number; }
interface ClientUsageSummary { clientId: string; email: string; twilioNumber: string; messagesSent: number; messagesReceived: number; segments: number; cost: number; }
interface UsageRecord { id: string; clientId: string; periodStart: string; periodEnd: string; messagesSent: number; messagesReceived: number; segments: number; cost: number; collectedAt: string; }
interface UsageTrigger { id: string; clientId: string; triggerType: string; threshold: number; twilioTriggerSid: string; status: 'active' | 'fired' | 'disabled'; createdAt: string; }
type DatePreset = 'today' | 'this_week' | 'this_month' | 'custom';

function getDateRange(preset: DatePreset): { startDate: string; endDate: string } {
  const now = new Date(); const end = now.toISOString().split('T')[0];
  switch (preset) {
    case 'today': return { startDate: end, endDate: end };
    case 'this_week': { const d = now.getDay(); const diff = now.getDate() - d + (d === 0 ? -6 : 1); const s = new Date(now); s.setDate(diff); return { startDate: s.toISOString().split('T')[0], endDate: end }; }
    case 'this_month': { const s = new Date(now.getFullYear(), now.getMonth(), 1); return { startDate: s.toISOString().split('T')[0], endDate: end }; }
    default: return { startDate: '', endDate: '' };
  }
}
function formatCost(cost: number): string { return `$${cost.toFixed(4)}`; }
function formatDate(dateStr: string): string { return dateStr ? new Date(dateStr).toLocaleDateString() : ''; }

const thStyle = { fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase' as const, color: '#475569', letterSpacing: 0.5 };

const AdminUsageDashboard: React.FC = () => {
  const [datePreset, setDatePreset] = useState<DatePreset>('this_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [totals, setTotals] = useState<UsageTotals | null>(null);
  const [perClient, setPerClient] = useState<ClientUsageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientUsageSummary | null>(null);
  const [clientHistory, setClientHistory] = useState<UsageRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [triggers, setTriggers] = useState<UsageTrigger[]>([]);
  const [triggersLoading, setTriggersLoading] = useState(false);
  const [createTriggerOpen, setCreateTriggerOpen] = useState(false);
  const [triggerForm, setTriggerForm] = useState({ clientId: '', triggerType: 'sms-outbound', threshold: '', callbackUrl: '' });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [collecting, setCollecting] = useState(false);

  const showSnackbar = (message: string, severity: 'success' | 'error') => { setSnackbar({ open: true, message, severity }); };

  const getFilterParams = useCallback(() => {
    if (datePreset === 'custom') return { startDate: customStart, endDate: customEnd };
    return getDateRange(datePreset);
  }, [datePreset, customStart, customEnd]);

  const fetchUsageData = useCallback(async () => {
    try { setLoading(true); setError(''); const r = await axios.get('/api/admin/usage', { params: getFilterParams() }); setTotals(r.data.totals); setPerClient(r.data.perClient); }
    catch (err: any) { setError(err.response?.data?.error || 'Failed to load usage data'); }
    finally { setLoading(false); }
  }, [getFilterParams]);

  const fetchClientHistory = useCallback(async (clientId: string) => {
    try { setHistoryLoading(true); const r = await axios.get(`/api/admin/usage/${clientId}`, { params: getFilterParams() }); setClientHistory(r.data.usage); }
    catch (err: any) { showSnackbar(err.response?.data?.error || 'Failed', 'error'); }
    finally { setHistoryLoading(false); }
  }, [getFilterParams]);

  const fetchTriggers = useCallback(async (clientId?: string) => {
    try { setTriggersLoading(true); const r = await axios.get('/api/admin/usage/triggers', { params: clientId ? { clientId } : {} }); setTriggers(r.data.triggers); }
    catch (err: any) { showSnackbar(err.response?.data?.error || 'Failed', 'error'); }
    finally { setTriggersLoading(false); }
  }, []);

  useEffect(() => { fetchUsageData(); fetchTriggers(); }, [fetchUsageData, fetchTriggers]);

  const handleCollectNow = async () => {
    try { setCollecting(true); const r = await axios.post('/api/admin/usage/collect'); showSnackbar(`Collected for ${r.data.collected} client(s)`, r.data.errors > 0 ? 'error' : 'success'); fetchUsageData(); }
    catch (err: any) { showSnackbar(err.response?.data?.error || 'Failed', 'error'); }
    finally { setCollecting(false); }
  };

  const handleClientClick = (client: ClientUsageSummary) => { setSelectedClient(client); fetchClientHistory(client.clientId); fetchTriggers(client.clientId); };
  const handleBackToOverview = () => { setSelectedClient(null); setClientHistory([]); fetchTriggers(); };

  const handleCreateTrigger = async () => {
    try { await axios.post('/api/admin/usage/triggers', { clientId: triggerForm.clientId, triggerType: triggerForm.triggerType, threshold: Number(triggerForm.threshold), callbackUrl: triggerForm.callbackUrl }); showSnackbar('Trigger created', 'success'); setCreateTriggerOpen(false); setTriggerForm({ clientId: '', triggerType: 'sms-outbound', threshold: '', callbackUrl: '' }); fetchTriggers(selectedClient?.clientId); }
    catch (err: any) { showSnackbar(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleDeleteTrigger = async (triggerId: string) => {
    try { await axios.delete(`/api/admin/usage/triggers/${triggerId}`); showSnackbar('Trigger deleted', 'success'); fetchTriggers(selectedClient?.clientId); }
    catch (err: any) { showSnackbar(err.response?.data?.error || 'Failed', 'error'); }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '1.75rem', color: '#0f172a', mb: 0.5 }}>Usage Dashboard</Typography>
      </Box>

      {/* Date Filter */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Date Range</InputLabel>
          <Select value={datePreset} label="Date Range" onChange={(e: SelectChangeEvent<DatePreset>) => setDatePreset(e.target.value as DatePreset)} sx={{ borderRadius: 2 }}>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="this_week">This Week</MenuItem>
            <MenuItem value="this_month">This Month</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>
        {datePreset === 'custom' && (
          <>
            <TextField size="small" type="date" label="Start" value={customStart} onChange={(e) => setCustomStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField size="small" type="date" label="End" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          </>
        )}
        <Tooltip title="Refresh"><IconButton onClick={fetchUsageData} disabled={loading} sx={{ color: '#475569' }}><RefreshIcon /></IconButton></Tooltip>
        <Tooltip title="Collect from Twilio"><IconButton onClick={handleCollectNow} disabled={collecting} sx={{ color: '#4b2bee' }}>{collecting ? <CircularProgress size={20} /> : <CollectIcon />}</IconButton></Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#4b2bee' }} /></Box>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      ) : selectedClient ? (
        <>
          {/* Client Detail */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <IconButton onClick={handleBackToOverview} sx={{ color: '#475569' }}><ArrowBackIcon /></IconButton>
            <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '1.125rem', color: '#0f172a' }}>Usage — {selectedClient.email}</Typography>
          </Box>
          {historyLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} sx={{ color: '#4b2bee' }} /></Box> : (
            <Box sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0px 12px 32px rgba(15, 23, 42, 0.04)', overflow: 'hidden', mb: 3 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>{['Period Start', 'Period End', 'Sent', 'Received', 'Segments', 'Cost'].map(h => <TableCell key={h} align={h !== 'Period Start' && h !== 'Period End' ? 'right' : 'left'} sx={thStyle}>{h}</TableCell>)}</TableRow></TableHead>
                  <TableBody>
                    {clientHistory.length === 0 ? <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#475569' }}>No records.</TableCell></TableRow> :
                      clientHistory.map(r => (
                        <TableRow key={r.id} sx={{ '&:hover': { bgcolor: 'rgba(75,43,238,0.04)' } }}>
                          <TableCell sx={{ color: '#0f172a' }}>{formatDate(r.periodStart)}</TableCell>
                          <TableCell sx={{ color: '#0f172a' }}>{formatDate(r.periodEnd)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}>{r.messagesSent}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}>{r.messagesReceived}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}>{r.segments}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}>{formatCost(r.cost)}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {renderTriggers()}
        </>
      ) : (
        <>
          {/* Metrics Cards */}
          {totals && (
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              {[
                { label: 'Messages Sent', value: totals.messagesSent },
                { label: 'Messages Received', value: totals.messagesReceived },
                { label: 'Total Cost', value: formatCost(totals.cost) },
                { label: 'Active Clients', value: totals.activeClients },
              ].map(m => (
                <Grid item xs={12} sm={6} md={3} key={m.label}>
                  <Box sx={{ bgcolor: '#ffffff', borderRadius: 3, p: 3, boxShadow: '0px 12px 32px rgba(15, 23, 42, 0.04)' }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', color: '#475569', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500, mb: 0.5 }}>{m.label}</Typography>
                    <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '1.5rem', color: '#0f172a' }}>{m.value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Per-Client Table */}
          <Box sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0px 12px 32px rgba(15, 23, 42, 0.04)', overflow: 'hidden', mb: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>{['Client', 'Twilio Number', 'Sent', 'Received', 'Segments', 'Cost'].map(h => <TableCell key={h} align={!['Client', 'Twilio Number'].includes(h) ? 'right' : 'left'} sx={thStyle}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>
                  {perClient.length === 0 ? <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#475569' }}>No data.</TableCell></TableRow> :
                    perClient.map(c => (
                      <TableRow key={c.clientId} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(75,43,238,0.04)' } }} onClick={() => handleClientClick(c)}>
                        <TableCell sx={{ color: '#0f172a', fontWeight: 500 }}>{c.email}</TableCell>
                        <TableCell sx={{ color: '#475569' }}>{c.twilioNumber}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}>{c.messagesSent}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}>{c.messagesReceived}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}>{c.segments}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}>{formatCost(c.cost)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          {renderTriggers()}
        </>
      )}

      {/* Create Trigger Dialog */}
      <Dialog open={createTriggerOpen} onClose={() => setCreateTriggerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>Create Usage Trigger</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Client ID" value={triggerForm.clientId} onChange={(e) => setTriggerForm({ ...triggerForm, clientId: e.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select value={triggerForm.triggerType} label="Type" onChange={(e) => setTriggerForm({ ...triggerForm, triggerType: e.target.value })}>
              <MenuItem value="sms-outbound">SMS Outbound</MenuItem>
              <MenuItem value="sms-inbound">SMS Inbound</MenuItem>
              <MenuItem value="sms">SMS (All)</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label="Threshold" type="number" value={triggerForm.threshold} onChange={(e) => setTriggerForm({ ...triggerForm, threshold: e.target.value })} />
          <TextField fullWidth margin="normal" label="Callback URL" value={triggerForm.callbackUrl} onChange={(e) => setTriggerForm({ ...triggerForm, callbackUrl: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTriggerOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateTrigger} disabled={!triggerForm.clientId || !triggerForm.threshold || !triggerForm.callbackUrl} sx={{ bgcolor: '#4b2bee', '&:hover': { bgcolor: '#3d1fd4' } }}>Create</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );

  function renderTriggers() {
    return (
      <Box sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0px 12px 32px rgba(15, 23, 42, 0.04)', p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '1.125rem', color: '#0f172a' }}>
            Usage Triggers {selectedClient ? `— ${selectedClient.email}` : ''}
          </Typography>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => { setTriggerForm({ clientId: selectedClient?.clientId || '', triggerType: 'sms-outbound', threshold: '', callbackUrl: '' }); setCreateTriggerOpen(true); }}
            sx={{ borderColor: '#4b2bee', color: '#4b2bee', textTransform: 'none', borderRadius: 2, '&:hover': { bgcolor: 'rgba(75,43,238,0.04)', borderColor: '#4b2bee' } }}
          >New Trigger</Button>
        </Box>
        {triggersLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} sx={{ color: '#4b2bee' }} /></Box> :
          triggers.length === 0 ? <Typography sx={{ color: '#475569', fontFamily: 'Inter, sans-serif', fontSize: '0.875rem' }}>No active triggers.</Typography> : (
            <Table size="small">
              <TableHead><TableRow>{['Client ID', 'Type', 'Threshold', 'Status', 'Actions'].map(h => <TableCell key={h} align={h === 'Threshold' || h === 'Actions' ? 'right' : 'left'} sx={thStyle}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {triggers.map(t => (
                  <TableRow key={t.id} sx={{ '&:hover': { bgcolor: 'rgba(75,43,238,0.04)' } }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#475569' }}>{t.clientId.slice(0, 8)}…</TableCell>
                    <TableCell sx={{ color: '#0f172a' }}>{t.triggerType}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}>{t.threshold}</TableCell>
                    <TableCell>
                      <Chip label={t.status} size="small" sx={{ bgcolor: t.status === 'active' ? 'rgba(0,184,148,0.1)' : t.status === 'fired' ? 'rgba(225,112,85,0.1)' : '#f1f5f9', color: t.status === 'active' ? '#00b894' : t.status === 'fired' ? '#e17055' : '#475569', fontWeight: 600, fontSize: '0.6875rem' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDeleteTrigger(t.id)} sx={{ color: '#e17055' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </Box>
    );
  }
};

export default AdminUsageDashboard;
